"use client"

import * as React from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { useLocale } from "@/i18n"
import {
  listProductFeatures,
  markProductFeatureSeen,
} from "@/features/product-features/services/product-features-api"
import type {
  ProductFeature,
  ResolvedFeatureDto,
} from "@/features/product-features/model/product-feature-types"

export const PRODUCT_FEATURES_ROOT_KEY = ["product-features"] as const

/**
 * Locale is part of the key because it is part of the ANSWER: the tutorial that comes back is chosen by
 * language on the server, so a French user and an English one are asking different questions. Not keyed by
 * workspace or branch — how a feature is presented is a fact about the product, identical for every church,
 * and a workspace-scoped key here would fetch the same rows once per tenant for nothing.
 */
export const buildProductFeaturesQueryKey = (locale: string) =>
  [...PRODUCT_FEATURES_ROOT_KEY, locale] as const

/** Badges and tutorials change on an operator's schedule, not a user's. Five minutes is generous. */
const STALE_TIME_MS = 5 * 60 * 1000

export function useProductFeaturesQuery() {
  const locale = useLocale()
  return useQuery({
    queryKey: buildProductFeaturesQueryKey(locale),
    queryFn: () => listProductFeatures(locale),
    staleTime: STALE_TIME_MS,
    // Nothing here is worth interrupting someone's work for.
    refetchOnWindowFocus: false,
    retry: 1,
  })
}

/**
 * One feature's resolved presentation, or null.
 *
 * Null while loading as well as when there is nothing to say — which is what lets every caller render
 * nothing without a loading state. A badge that flickers in after the page settles is worse than one that
 * arrives with the next navigation.
 */
export function useProductFeature(feature: ProductFeature): ResolvedFeatureDto | null {
  const { data } = useProductFeaturesQuery()
  return React.useMemo(
    () => data?.items.find((item) => item.key === feature) ?? null,
    [data, feature],
  )
}

/** Every feature that has something to say, keyed for lookup — used by the sidebar in one pass. */
export function useProductFeatureMap(): ReadonlyMap<string, ResolvedFeatureDto> {
  const { data } = useProductFeaturesQuery()
  return React.useMemo(() => {
    const map = new Map<string, ResolvedFeatureDto>()
    for (const item of data?.items ?? []) map.set(item.key, item)
    return map
  }, [data])
}

/**
 * Records that this user has opened a feature, once.
 *
 * Deliberately silent on failure. The worst outcome is that a badge shows again tomorrow, and a toast
 * reading "could not record feature view" would be a support ticket about nothing.
 */
export function useMarkProductFeatureSeen() {
  const queryClient = useQueryClient()
  const locale = useLocale()

  return useMutation({
    mutationFn: (key: ProductFeature) => markProductFeatureSeen(key),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: buildProductFeaturesQueryKey(locale) })
    },
    onError: () => {
      /* Intentionally silent — see above. */
    },
  })
}
